import { TweenMax } from 'gsap/TweenMax'
import * as PIXI from 'pixi.js'
import Scene1 from './scene1.js'
import Scene2 from './scene2.js'
import Scene3 from './scene3.js'
import Scene4 from './scene4.js'
import keyboard from './keyboard.js'
import track from './track.js'

const space = keyboard(32)

class ScenesManager {
  constructor (game) {
    this.game = game
    this.scene1 = new Scene1(game)
    this.scene2 = new Scene2(game)
    this.scene3 = new Scene3(game)
    this.scene4 = new Scene4(game)

    this.pauseCount = 0
  }

  createOverlay () {
    const overlay = new PIXI.Graphics()
    overlay.beginFill(0x000000, 0.85)
    overlay.drawRect(0, 0, this.game.app.renderer.realWidth, this.game.app.renderer.realHeight)
    overlay.endFill()
    return overlay
  }

  createPlay () {
    const play = new PIXI.Sprite(PIXI.Texture.from('play'))
    play.scale.set(0.25)
    const x = ~~(this.game.app.renderer.realWidth - play.width) / 2
    const y = ~~(this.game.app.renderer.realHeight - play.height) / 2
    play.x = x
    play.y = y
    return play
  }

  createPause () {
    const pause = new PIXI.Sprite(PIXI.Texture.from('pause'))
    pause.scale.set(0.25)
    const x = ~~(this.game.app.renderer.realWidth - pause.width) / 2
    const y = ~~(this.game.app.renderer.realHeight - pause.height) / 2
    pause.x = x
    pause.y = y
    return pause
  }

  init () {
    this.go(1)
  }

  go (index, score) {
    if (index === 1) {
      this.scene1.enter()
      this.game.app.stage.interactive = true
      this.game.app.stage.on('click', this.onLeaveScene1, this)
      this.game.app.stage.on('touchend', this.onLeaveScene1, this)

      if (this.game.audioEnabled) {
        PIXI.sound.play('audioBGM1')
      }

      track('page', { path: '/scene-1' })
    } else if (index === 2) {
      this.scene2.enter()
      this.game.app.stage.interactive = true
      this.game.app.stage.on('pointerdown', this.onLeaveScene2, this)

      track('page', { path: '/scene-2' })
    } else if (index === 3) {
      PIXI.sound.stop('audioBGM1')

      this.scene3.enter()
      track('page', { path: '/scene-3' })

      this.overlay = this.createOverlay()
      this.play = this.createPlay()
      this.pause = this.createPause()

      this.game.app.stage.interactive = true
      this.scene3.onGameStart = () => {
        // 倒數計時器完成倒數並且遊戲正式開始
        track('event', 'game_start', {
          category: this.game.id,
          label: 'timer_end'
        })

        this.game.app.stage.on('pointerdown', this.onTouchScene3, this)
        space.press = () => {
          this.toggleGamePauseState()
        }
      }
      this.scene3.onLeaveScene3 = this.onLeaveScene3.bind(this)
      this.scene3.onGameOver = (score, player) => {
        const sound = PIXI.sound.play('audioGameOver')
        sound.volume = 0.4
        this.go(4, score)
        this.game.app.stage.interactive = false
        this.game.app.stage.off('pointerdown', this.onTouchScene3)
        space.press = null
        this.play.destroy()
        this.pause.destroy()
        this.overlay.destroy()
        this.play = null
        this.pause = null
        this.overlay = null

        // GA 紀錄分數
        track('event', 'gameOver_playingScore', {
          category: this.game.id,
          label: Math.floor(score).toString()
        })

        // GA 送出遊戲遊玩時間 in second
        const duration = Math.round((this.scene3.endingTimestamp - this.scene3.startedTimestamp) / 1000)
        track('event', 'gameOver_playingTime', {
          category: this.game.id,
          label: duration
        })

        // GA 送出踩到羊墊總數
        track('event', 'gameOver_countSheeps', {
          category: this.game.id,
          label: player.hitsSheepCount
        })

        // GA 送出踩到的墊子總數
        track('event', 'gameOver_countStairs', {
          category: this.game.id,
          label: player.jumpCount
        })

        // GA 送出暫停次數
        track('event', 'gameOver_pauseTimes', {
          category: this.game.id,
          label: this.pauseCount.toString()
        })
        this.pauseCount = 0
      }
    } else if (index === 4) {
      track('page', { path: '/scene-4' })
      this.scene4.enter(score)
      this.scene4.onPlayAgain = () => {
        this.scene3.destroy()
        this.scene4.leave()
        this.go(3)
      }
    }
  }

  onLeaveScene1 () {
    if (this.game.audioEnabled) {
      PIXI.sound.play('audioPop')
    }

    track('event', 'introCampaign_button', {
      category: this.game.id,
      label: 'click'
    })

    this.game.app.stage.interactive = false
    this.game.app.stage.off('click', this.onLeaveScene1)
    this.game.app.stage.off('touchend', this.onLeaveScene1)
    this.scene1.leave()
    this.go(2)
  }

  onLeaveScene2 () {
    if (this.game.audioEnabled) {
      PIXI.sound.play('audioPop')
    }

    track('event', 'introFunction_button', {
      category: this.game.id,
      label: 'click'
    })

    this.game.app.stage.interactive = false
    this.game.app.stage.off('pointerdown', this.onLeaveScene2)
    this.scene2.leave()
    this.go(3)
  }

  onTouchScene3 () {
    this.toggleGamePauseState()
  }

  onLeaveScene3 (callback) {
    if (callback) {
      callback()
    }

    this.scene3.leave()
  }

  toggleGamePauseState () {
    if (this.scene3.updater.isPaused) {
      this.scene3.updater.play(() => {
        try {
          PIXI.sound.resumeAll()
        } catch (err) {
          //
        }
        this.game.background.ticker.start()
      })
      this.game.app.stage.removeChild(this.pause, this.overlay)
      this.game.app.stage.addChild(this.play)
      this.anime = TweenMax.to(this.play, 0.6, {
        delay: 0.4,
        pixi: {
          alpha: 0
        },
        onComplete: () => {
          this.anime = null
          if (this.play) {
            this.game.app.stage.removeChild(this.play)
            this.play.alpha = 1
          }
        }
      })
    } else {
      this.pauseCount++

      this.scene3.updater.pause(() => {
        try {
          PIXI.sound.pauseAll()
        } catch (err) {
          //
        }
        this.game.background.ticker.stop()
      })
      this.game.app.stage.removeChild(this.play)
      this.game.app.stage.addChild(this.overlay, this.pause)

      if (this.anime) {
        TweenMax.killTweensOf(this.anime)
      }
    }
  }
}

export default ScenesManager
